import React, { useContext, useEffect, useState } from "react";
import GlobalContext from "../context/GlobalContext";
import LabelsService from "../services/labels-service";
import { AuthContext } from "react-oauth2-code-pkce";
import { IconEdit, IconTrash } from "./Icons";

const presetColors = [
  "#6366f1", // indigo
  "#6b7280", // gray
  "#22c55e", // green
  "#3b82f6", // blue
  "#ef4444", // red
  "#a21caf", // purple
];

function LabelPopup({
  open, onClose, onSave, initialName = "", initialColor = presetColors[0], initialPhone = "", loading = false, title = "Persoon of shift toevoegen"
}) {
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor);
  const [phoneNumber, setPhoneNumber] = useState(initialPhone);
  useEffect(() => {
    setName(initialName);
    setColor(initialColor);
    setPhoneNumber(initialPhone);
  }, [initialName, initialColor, initialPhone, open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl p-6 min-w-[320px]">
        <div className="flex items-start justify-between mb-4">
          <h2 className="font-semibold text-lg text-gray-800 dark:text-gray-100">{title}</h2>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Sluiten" disabled={loading}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
        <input
          className="input-field mb-2"
          placeholder="Naam"
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
        />
        <input
          className="input-field mb-3"
          placeholder="Doorschakelnummer"
          value={phoneNumber}
          onChange={e => setPhoneNumber(e.target.value)}
        />
        <div className="flex gap-2 mb-4">
          {presetColors.map((c) => (
            <span
              key={c}
              className={`w-6 h-6 rounded-full border-2 cursor-pointer ${color === c ? 'border-blue-600' : 'border-transparent'}`}
              style={{ background: c }}
              title={c}
              onClick={() => setColor(c)}
            />
          ))}
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            className="w-7 h-7 p-0 border-0 bg-transparent cursor-pointer"
            title="Kies een kleur"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            className="btn-ghost"
            onClick={onClose}
            type="button"
            disabled={loading}
          >Annuleer</button>
          <button
            className="btn-primary"
            onClick={() => onSave(name, color, phoneNumber)}
            type="button"
            disabled={!name.trim() || !phoneNumber.trim() || loading}
          >Opslaan</button>
        </div>
      </div>
    </div>
  );
}

export default function Labels() {
  const { 
    labels, 
    setLabels, 
    activeCalendarId, 
    cacheLabelsForCalendar,
    getCachedLabelsForCalendar,
    isReadonly,
    fetchEvents
  } = useContext(GlobalContext);
  const { token, tokenData } = useContext(AuthContext);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editLabel, setEditLabel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Always clear labels first when calendar changes
    setLabels([]);
    setError(null);
    
    if (!token || !activeCalendarId) {
      return;
    }
    
    console.log('Fetching labels for calendar:', activeCalendarId); // Debug log
    
    // Check cache first
    const cachedLabels = getCachedLabelsForCalendar(activeCalendarId);
    if (cachedLabels) {
      console.log('Using cached labels for calendar:', activeCalendarId, cachedLabels); // Debug log
      setLabels(cachedLabels.map(label => ({ ...label, visible: true })));
      return;
    }
    
    console.log('No cached labels found, fetching from API for calendar:', activeCalendarId); // Debug log
    LabelsService.fetchLabels(activeCalendarId, token)
      .then(apiLabels => {
        console.log('Fetched labels from API:', apiLabels); // Debug log
        const labelsWithVisible = apiLabels.map(label => ({ ...label, visible: true }));
        setLabels(labelsWithVisible);
        // Cache the labels for this calendar
        cacheLabelsForCalendar(activeCalendarId, apiLabels);
      })
      .catch((err) => {
        console.error('Failed to fetch labels:', err);
        setLabels([]);
        setError("Kan personen of shifts niet laden voor deze kalender.");
      });
    // eslint-disable-next-line
  }, [token, activeCalendarId]);

  // Add label
  const handleAdd = async (name, color, phoneNumber) => {
    if (!activeCalendarId) {
      setError("Geen kalender geselecteerd.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const label = await LabelsService.createLabel({ name, color, phoneNumber, calendarId: activeCalendarId }, token);
      const newLabels = [...labels, { ...label, visible: true }];
      setLabels(newLabels);
      // Update cache
      cacheLabelsForCalendar(activeCalendarId, newLabels.map(l => ({ ...l, visible: undefined })));
      setShowAddPopup(false);
    } catch (e) {
      if (e.message.includes('403')) {
        setError("Geen toegang tot deze kalender.");
      } else if (e.message.includes('404')) {
        setError("Kalender niet gevonden.");
      } else if (e.message.includes('400')) {
        setError("Ongeldige kalender of gegevens.");
      } else {
        setError("Kan persoon of shift niet toevoegen.");
      }
    }
    setLoading(false);
  };

  // Delete label
  const handleDelete = async (id) => {
    if (!window.confirm("Verwijder deze persoon of shift?")) return;
    setError(null);
    try {
      await LabelsService.deleteLabel(id, token);
      const updatedLabels = labels.filter(l => l.id !== id);
      setLabels(updatedLabels);
      // Update cache
      cacheLabelsForCalendar(activeCalendarId, updatedLabels.map(l => ({ ...l, visible: undefined })));
    } catch (e) {
      if (e.message.includes('403')) {
        setError("Geen toegang om dit item te verwijderen.");
      } else if (e.message.includes('404')) {
        setError("Persoon of shift niet gevonden.");
      } else {
        setError("Kan persoon of shift niet verwijderen.");
      }
    }
  };

  // Edit label
  const handleEditSave = async (name, color, phoneNumber) => {
    if (!editLabel) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await LabelsService.updateLabel(editLabel.id, { name, color, phoneNumber, calendarId: activeCalendarId }, token);
      const updatedLabels = labels.map(l => l.id === editLabel.id ? { ...updated, visible: l.visible } : l);
      setLabels(updatedLabels);
      // Update cache
      cacheLabelsForCalendar(activeCalendarId, updatedLabels.map(l => ({ ...l, visible: undefined })));
      setShowEditPopup(false);
      setEditLabel(null);
      if (typeof fetchEvents === "function") {
        await fetchEvents();
      }
    } catch (e) {
      if (e.message.includes('403')) {
        setError("Geen toegang om dit item te bewerken.");
      } else if (e.message.includes('404')) {
        setError("Persoon of shift niet gevonden.");
      } else if (e.message.includes('400')) {
        setError("Ongeldige gegevens.");
      } else {
        setError("Kan persoon of shift niet bijwerken.");
      }
    }
    setLoading(false);
  };

  return (
    <div>
      <p className="section-label">Personen / shifts</p>
      {error && <div className="text-red-500 text-xs mb-2">{error}</div>}
      {!isReadonly && (
        <button
          type="button"
          onClick={() => setShowAddPopup(true)}
          className="btn-ghost w-full gap-2 py-2.5 mt-2 mb-1"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
            <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
          Nieuwe persoon / shift
        </button>
      )}
      <LabelPopup
        open={showAddPopup}
        onClose={() => setShowAddPopup(false)}
        onSave={handleAdd}
        title="Persoon of shift toevoegen"
        loading={loading}
      />
      <LabelPopup
        open={showEditPopup}
        onClose={() => { setShowEditPopup(false); setEditLabel(null); }}
        onSave={handleEditSave}
        initialName={editLabel ? editLabel.name : ""}
        initialColor={editLabel ? editLabel.color : presetColors[0]}
        initialPhone={editLabel ? (editLabel.phoneNumber || "") : ""}
        title="Persoon of shift bewerken"
        loading={loading}
      />
      {/* List labels */}
      {labels && labels.length > 0 ? labels.filter(l => l.id).map((labelObj) => (
        <div
          key={labelObj.id}
          className={`flex items-start gap-2 mt-1 rounded-md px-1 py-1 hover:bg-gray-50 dark:hover:bg-gray-800 ${!isReadonly ? 'cursor-grab active:cursor-grabbing' : ''} ${labelObj.visible === false ? 'opacity-40' : ''}`}
          draggable={!isReadonly}
          onDragStart={isReadonly ? undefined : (e) => {
            const payload = JSON.stringify({
              id: labelObj.id,
              name: labelObj.name,
              color: labelObj.color,
              phoneNumber: labelObj.phoneNumber,
            });
            e.dataTransfer.setData("application/x-onduty-roster", payload);
            e.dataTransfer.setData("text/plain", "roster:" + payload);
            e.dataTransfer.effectAllowed = "copy";
          }}
          title={!isReadonly ? "Sleep naar een dag om in te plannen" : labelObj.name}
        >
          {!isReadonly && (
            <span className="text-gray-300 dark:text-gray-600 mt-1 flex-shrink-0" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
                <circle cx="5" cy="3" r="1.25" />
                <circle cx="11" cy="3" r="1.25" />
                <circle cx="5" cy="8" r="1.25" />
                <circle cx="11" cy="8" r="1.25" />
                <circle cx="5" cy="13" r="1.25" />
                <circle cx="11" cy="13" r="1.25" />
              </svg>
            </span>
          )}
          <button
            type="button"
            className={`w-4 h-4 rounded-full mt-1 flex-shrink-0 cursor-pointer p-0 ${
              labelObj.visible === false
                ? "border-2 border-dashed border-gray-400"
                : "border-2 border-white dark:border-gray-900"
            }`}
            style={{
              background: labelObj.color || "#ccc",
              boxShadow: labelObj.visible === false ? undefined : `0 0 0 2px ${labelObj.color || "#ccc"}`,
            }}
            title="Tonen of verbergen op de kalender"
            aria-label="Tonen of verbergen op de kalender"
            aria-pressed={labelObj.visible !== false}
            onClick={(e) => {
              e.stopPropagation();
              setLabels((current) =>
                current.map((l) =>
                  l.id === labelObj.id ? { ...l, visible: l.visible === false } : l
                )
              );
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-gray-700 dark:text-gray-200 block truncate">{labelObj.name}</span>
            {labelObj.phoneNumber && (
              <span className="text-[11px] leading-tight text-gray-400 block whitespace-nowrap">{labelObj.phoneNumber}</span>
            )}
          </div>
          {!isReadonly && (
            <div className="flex items-center gap-0.5 flex-shrink-0 self-start h-6 mt-0.5">
              <button
                className="w-6 h-6 inline-flex items-center justify-center text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-800 rounded"
                onClick={() => { setEditLabel(labelObj); setShowEditPopup(true); }}
                onMouseDown={(e) => e.stopPropagation()}
                type="button"
                title="Bewerken"
                aria-label="Bewerken"
              >
                <IconEdit />
              </button>
              <button
                className="w-6 h-6 inline-flex items-center justify-center text-red-600 hover:bg-red-50 dark:hover:bg-gray-800 rounded"
                onClick={() => handleDelete(labelObj.id)}
                onMouseDown={(e) => e.stopPropagation()}
                type="button"
                title="Verwijderen"
                aria-label="Verwijderen"
              >
                <IconTrash />
              </button>
            </div>
          )}
        </div>
      )) : (
        <div className="text-gray-400 text-xs mt-2">
          {activeCalendarId ? 'Nog geen personen of shifts voor deze kalender' : 'Selecteer een kalender'}
        </div>
      )}
    </div>
  );
}
